import { c as _c } from "react/compiler-runtime";
import { ProfileUI } from 'components/portal/profileTypes';
import { usePortal } from 'components/portal/portal';
import { SubqStatus, zSubqCfg, SubqCfg } from 'shared/forecast/subq';
import { Json, zx } from 'shared/type';
import { compactObj } from 'shared/collection';
import { Database } from 'utils/db/tables.generated';
import { useQuerySbSync } from 'utils/db/useQuerySb';
import { StatusMd } from 'shared/forecast/questionStatus';
import { z } from 'zod';
import { QuestionCfg } from 'shared/forecast/question';
import { ReactionSummaryOfType } from './reactions';
export const subqStatusMd: Record<SubqStatus, StatusMd> = {
  draft: {
    desc: 'Not yet ready to be considered',
    color: 'slate',
    defaultNextStatus: 'proposed'
  },
  proposed: {
    desc: 'Ready to be reviewed for approval',
    color: 'cyan',
    forecast: {
      visible: true
    },
    displayOrder: 2,
    verb: 'Propose',
    defaultNextStatus: 'approved'
  },
  approved: {
    verb: 'Approve',
    desc: 'Final review of question, causes and consequences before being open for forecasting.',
    color: 'emerald',
    forecast: {
      visible: true
    },
    displayOrder: 1
  },
  paused: {
    desc: 'Was approved, but now temporarily not open for forecasting',
    color: 'rose',
    forecast: {
      visible: true,
      readonly: true
    },
    displayOrder: 3,
    defaultNextStatus: 'approved'
  },
  /** deprecated, we track resolutions independently of status */
  resolved: {
    verb: 'resolve',
    desc: 'The question has been resolved',
    color: 'stone',
    forecast: {
      visible: true,
      readonly: true
    },
    displayOrder: 4,
    hide: true
  },
  /** deprecated, not clear what this was supposed to do */
  closed: {
    desc: 'Closed without expectation of being resolved',
    color: 'stone',
    forecast: {
      visible: true,
      readonly: true
    },
    displayOrder: 5,
    hide: true
  }
};
export const approvedStatuses = ['approved', 'paused'] as SubqStatus[];
export const isApproved = (withStatus: {
  status: SubqStatus;
}) => approvedStatuses.includes(withStatus.status);
export const zSubqRow = z.object({
  id: z.string(),
  question_id: z.string(),
  parent_id: zx.nullish.string,
  cfg: zSubqCfg,
  deleted: zx.nullishToUndefined(z.boolean().default(false)),
  created_at: z.string(),
  created_by: z.string(),
  updated_at: z.string()
});
export type SubqRow = z.infer<typeof zSubqRow>;
export type SubqItem = SubqRow & {
  reactions: ReactionSummaryOfType<'vote'> | undefined;
  profile?: ProfileUI;
  readonly?: boolean;
};
export const parseSubqCfg = (cfg: Json) => zSubqCfg.parse(cfg);
export type SubqFetched = {
  type: 'fetched';
} & SubqRow;
type SubquestionTable = Database['public']['Tables']['subquestion'];
export type SubqNew = {
  type: 'new';
} & Omit<SubquestionTable['Insert'], 'cfg'> & {
  cfg: SubqCfg;
};
export type SubqMissing = {
  type: 'missing';
};
export type SubqAny = SubqFetched | SubqNew | SubqMissing;
export const useSubqQuery = t0 => {
  const $ = _c(16);
  const {
    question_id,
    subquestion_id,
    parent_id,
    status,
    showDraft,
    groupsOnly,
    disabled,
    pick
  } = t0;
  const {
    profile
  } = usePortal();
  let t1;
  if ($[0] !== question_id) {
    t1 = compactObj({
      question_id,
      deleted: false
    });
    $[0] = question_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== groupsOnly || $[3] !== parent_id || $[4] !== profile?.id || $[5] !== showDraft || $[6] !== status || $[7] !== subquestion_id) {
    t2 = r => (showDraft || r.created_by == profile?.id || !["draft-admin", "draft"].includes(r.cfg.status)) && (!subquestion_id || r.id == subquestion_id) && (parent_id === undefined || r.parent_id == parent_id) && (!status || status.includes(r.cfg.status)) && (!groupsOnly || r.cfg.childrenEnabled);
    $[2] = groupsOnly;
    $[3] = parent_id;
    $[4] = profile?.id;
    $[5] = showDraft;
    $[6] = status;
    $[7] = subquestion_id;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = !disabled && question_id != null;
  let t4;
  if ($[9] !== t3) {
    t4 = {
      enabled: t3
    };
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== pick || $[12] !== t1 || $[13] !== t2 || $[14] !== t4) {
    t5 = {
      table: "subquestion",
      filter: t1,
      clientFilter: t2,
      schema: zSubqRow,
      options: t4,
      pick
    };
    $[11] = pick;
    $[12] = t1;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return useQuerySbSync(t5);
};
export const useQuestionAutoComplete = (question: Pick<QuestionCfg, 'title' | 'subTitle'>) => {};